import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Users, 
  Calendar, 
  TrendingUp, 
  ArrowRight, 
  Star, 
  CheckCircle,
  Play,
  Award,
  User,
  Building,
  MapPin,
  Briefcase,
  Trophy,
  Lightbulb
} from 'lucide-react';
import { profileAPI, mentorAPI } from '@/services/api';
import api, { eventsAPI, hackathonAPI } from '@/services/api';
import { HOME_PAGE_DEFAULTS, mergeHomePageContent, HomePageContent } from '@/constants/homePageDefaults';
import { normalizeHeroCarouselOrder } from '@/utils/heroCarouselOrder';
import HeroCarouselTrack from '@/components/home/HeroCarouselTrack';
import { AnimatePresence, motion } from 'framer-motion';
import ResumeAnalysisTeaser from '@/components/home/ResumeAnalysisTeaser';

interface Sponsor {
  name: string;
  logoUrl: string;
  website: string;
  order: number;
}

const Home: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [sponsorsLoading, setSponsorsLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mentors, setMentors] = useState<any[]>([]);
  const [carouselEvents, setCarouselEvents] = useState<any[]>([]);
  const [carouselHackathons, setCarouselHackathons] = useState<any[]>([]);
  const [mentorImageUrls, setMentorImageUrls] = useState<{ [key: string]: string }>({});
  const [eventFlyerUrls, setEventFlyerUrls] = useState<{ [key: string]: string }>({});
  const [hackathonFlyerUrls, setHackathonFlyerUrls] = useState<{ [key: string]: string }>({});
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [eventActiveIndex, setEventActiveIndex] = useState(0);
  const [homePage, setHomePage] = useState<HomePageContent>(HOME_PAGE_DEFAULTS);

  const TICKER_ICONS = [Calendar, Award, Users] as const;

  const heroCarouselOrder = useMemo(
    () => normalizeHeroCarouselOrder(homePage.heroCarouselOrder),
    [homePage.heroCarouselOrder]
  );

  // Scroll to in-page sections when opened via /#anchor (e.g. carousel → resume upload)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  const totalSlides = useMemo(() => {
    let count = 0;
    for (const item of heroCarouselOrder) {
      if (!item.enabled) continue;
      switch (item.type) {
        case 'hackathons':
          if (carouselHackathons.length > 0) count += 1;
          break;
        case 'events':
          count += carouselEvents.length;
          break;
        case 'empowering_careers':
        case 'success_stories':
        case 'expert_mentors':
          count += 1;
          break;
        case 'custom':
          if (item.custom?.title?.trim()) count += 1;
          break;
        default:
          break;
      }
    }
    return count;
  }, [heroCarouselOrder, carouselHackathons.length, carouselEvents.length]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Function to get signed URL for event flyers
  const getSignedEventFlyerUrl = async (flyerUrl: string) => {
    try {
      if (!flyerUrl) return null;
      
      // Extract S3 key from URL
      const url = new URL(flyerUrl);
      const key = url.pathname.substring(1); // Remove leading slash
      
      // Try public endpoint first (no auth required)
      try {
        console.log('🖼️ Trying public event flyer endpoint for key:', key);
        const response = await api.get(`/upload/event-flyer-url/${encodeURIComponent(key)}`);
        console.log('✅ Public event flyer endpoint response:', response.data);
        console.log('🔍 Event flyer URL type:', typeof response.data.url);
        console.log('🔍 Event flyer URL value:', response.data.url);
        
        if (response.data.success && response.data.url) {
          // Handle both string and object formats
          if (typeof response.data.url === 'string') {
            return response.data.url;
          } else if (typeof response.data.url === 'object' && response.data.url.signedUrl) {
            console.log('🔄 Extracting signedUrl from object response');
            return response.data.url.signedUrl;
          }
        }
      } catch (publicError) {
        console.log('❌ Public endpoint failed for event flyer:', (publicError as Error).message);
      }
      
      // Try authenticated endpoint as fallback
      try {
        const response = await api.get(`/upload/file/${encodeURIComponent(key)}`);
        if (response.data.success && response.data.url) {
          return response.data.url;
        }
      } catch (authError) {
        console.error('Authenticated endpoint also failed for event flyer:', authError);
      }
      
      return flyerUrl; // Fallback to original URL
    } catch (error) {
      console.error('Error getting signed event flyer URL:', error);
      return flyerUrl; // Fallback to original URL
    }
  };

  // Function to get signed URL for hackathon flyers
  const getSignedHackathonFlyerUrl = async (flyerUrl: string) => {
    try {
      if (!flyerUrl) return null;
      
      // Extract S3 key from URL
      const url = new URL(flyerUrl);
      const key = url.pathname.substring(1); // Remove leading slash
      
      // Try public endpoint first (no auth required)
      try {
        console.log('🖼️ Trying public hackathon flyer endpoint for key:', key);
        const response = await api.get(`/upload/hackathon-flyer-url/${encodeURIComponent(key)}`);
        console.log('✅ Public hackathon flyer endpoint response:', response.data);
        
        if (response.data.success && response.data.url) {
          // Handle both string and object formats
          if (typeof response.data.url === 'string') {
            return response.data.url;
          } else if (typeof response.data.url === 'object' && response.data.url.signedUrl) {
            console.log('🔄 Extracting signedUrl from object response');
            return response.data.url.signedUrl;
          }
        }
      } catch (publicError) {
        console.log('❌ Public endpoint failed for hackathon flyer:', (publicError as Error).message);
      }
      
      // Try authenticated endpoint as fallback
      try {
        const response = await api.get(`/upload/file/${encodeURIComponent(key)}`);
        if (response.data.success && response.data.url) {
          return response.data.url;
        }
      } catch (authError) {
        console.error('Authenticated endpoint also failed for hackathon flyer:', authError);
      }
      
      return flyerUrl; // Fallback to original URL
    } catch (error) {
      console.error('Error getting signed hackathon flyer URL:', error);
      return flyerUrl; // Fallback to original URL
    }
  };



  // Fetch featured mentors
  useEffect(() => {
    const fetchMentors = async () => {
      try {
        console.log('🔐 Home: Authentication status:', isAuthenticated);
        console.log('👤 Home: User data:', user);
        
        const mentorData = await mentorAPI.getFeatured();
        setMentors(mentorData);
        
                // Fetch signed URLs for mentor profile images - works for both authenticated and public access
        const getSignedProfileImageUrl = async (imageUrl: string) => {
          try {
            if (!imageUrl) return null;
            
            // Extract S3 key from URL
            const url = new URL(imageUrl);
            const key = url.pathname.substring(1); // Remove leading slash
            
            // Try public endpoint first (no auth required)
            try {
              console.log('🖼️ Trying public mentor image endpoint for key:', key);
              const response = await api.get(`/mentor-image-url?key=${encodeURIComponent(key)}`);
              console.log('✅ Public mentor image endpoint response:', response.data);
              if (response.data.success && response.data.url) {
                return response.data.url;
              }
            } catch (publicError) {
              console.log('❌ Public endpoint failed for mentor image:', (publicError as Error).message);
            }
            
            // Try authenticated endpoint as fallback (for logged in users)
            try {
              const response = await api.get(`/upload/file/${encodeURIComponent(key)}`);
              if (response.data.success && response.data.url) {
                return response.data.url;
              }
            } catch (authError) {
              console.error('Authenticated endpoint also failed:', authError);
            }
            
            return imageUrl; // Fallback to original URL
          } catch (error) {
            console.error('Error getting signed profile image URL:', error);
            return imageUrl; // Fallback to original URL
          }
        };
        
        const imageUrls: { [key: string]: string } = {};
        for (const mentor of mentorData) {
          if (mentor.profileImageUrl) {
            try {
              const signedUrl = await getSignedProfileImageUrl(mentor.profileImageUrl);
              if (signedUrl) {
                // Signed URLs already have expiration built-in, no need for additional cache busting
                imageUrls[mentor._id] = signedUrl;
              }
            } catch (error) {
              console.error(`Error fetching signed URL for mentor ${mentor._id}:`, error);
            }
          }
        }
        setMentorImageUrls(imageUrls);
      } catch (error) {
        console.error('Error fetching mentors:', error);
        // Fallback to empty array if API fails
        setMentors([]);
      }
    };
    fetchMentors();
  }, []);

  // Fetch public CMS content (sponsors + home page copy)
  useEffect(() => {
    const fetchPublicContent = async () => {
      try {
        const response = await fetch('/api/admin/content/public');
        const data = await response.json();
        if (data.success) {
          if (data.data.homePage) {
            setHomePage(mergeHomePageContent(data.data.homePage));
          }
          if (data.data.sponsors) {
            setSponsors(data.data.sponsors);
          }
        }
      } catch (error) {
        console.error('Failed to fetch public content:', error);
      } finally {
        setSponsorsLoading(false);
      }
    };

    fetchPublicContent();

    const handleContentUpdate = () => {
      fetchPublicContent();
    };
    window.addEventListener('contentUpdated', handleContentUpdate);
    return () => window.removeEventListener('contentUpdated', handleContentUpdate);
  }, []);

  // Fetch carousel events
  useEffect(() => {
    const fetchCarouselEvents = async () => {
      try {
        const eventsData = await eventsAPI.getCarouselEvents();
        setCarouselEvents(eventsData);
        
        // Fetch signed URLs for event flyers
        const flyerUrls: { [key: string]: string } = {};
        for (const event of eventsData) {
          if (event.eventFlyer) {
            try {
              console.log(`🖼️ Processing event flyer for event ${event._id}:`, event.eventFlyer);
              const signedUrl = await getSignedEventFlyerUrl(event.eventFlyer);
              console.log(`✅ Got signed URL for event ${event._id}:`, signedUrl);
              if (signedUrl) {
                flyerUrls[event._id] = signedUrl;
              }
            } catch (error) {
              console.error(`Error fetching signed URL for event flyer ${event._id}:`, error);
            }
          }
        }
        console.log('📋 Final event flyer URLs:', flyerUrls);
        setEventFlyerUrls(flyerUrls);
      } catch (error) {
        console.error('Error fetching carousel events:', error);
        setCarouselEvents([]);
      }
    };
    fetchCarouselEvents();
  }, []);

  // Fetch carousel hackathons
  useEffect(() => {
    const fetchCarouselHackathons = async () => {
      try {
        const hackathonData = await hackathonAPI.getCarouselHackathons();
        setCarouselHackathons(hackathonData);

        // Fetch signed URLs for hackathon flyers
        const flyerUrls: { [key: string]: string } = {};
        for (const hackathon of hackathonData) {
          if (hackathon.hackathonFlyer) {
            try {
              console.log(`🖼️ Processing hackathon flyer for hackathon ${hackathon._id}:`, hackathon.hackathonFlyer);
              const signedUrl = await getSignedHackathonFlyerUrl(hackathon.hackathonFlyer);
              console.log(`✅ Got signed URL for hackathon ${hackathon._id}:`, signedUrl);
              if (signedUrl) {
                flyerUrls[hackathon._id] = signedUrl;
              }
            } catch (error) {
              console.error(`Error fetching signed URL for hackathon flyer ${hackathon._id}:`, error);
            }
          }
        }
        console.log('📋 Final hackathon flyer URLs:', flyerUrls);
        setHackathonFlyerUrls(flyerUrls);
      } catch (error) {
        console.error('Error fetching carousel hackathons:', error);
        setCarouselHackathons([]);
      }
    };
    fetchCarouselHackathons();
  }, []);


  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
              setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 8000); // Change slide every 8 seconds
    return () => clearInterval(timer);
  }, [totalSlides]);

  // Auto-advance events carousel
  useEffect(() => {
    if (carouselEvents.length > 1) {
      const timer = setInterval(() => {
        setEventActiveIndex((prev) => (prev + 1) % carouselEvents.length);
      }, 6000); // Change slide every 6 seconds
      return () => clearInterval(timer);
    }
  }, [carouselEvents.length]);



  // Touch handlers for swipe functionality
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
              setCurrentSlide((prev) => (prev + 1) % totalSlides);
    } else if (isRightSwipe) {
              setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    }

    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
  };

  const getDashboardLink = () => {
    switch (user?.role) {
      case 'admin':
        return '/admin/dashboard';
      case 'mentor':
        return '/mentor/dashboard';
      case 'aspirant':
        return '/aspirant/dashboard';
      default:
        return '/';
    }
  };

  const getProfileLink = () => {
    switch (user?.role) {
      case 'admin':
        return '/admin/profile';
      case 'mentor':
        return '/mentor/profile';
      case 'aspirant':
        return '/aspirant/profile';
      default:
        return '/';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* News Ticker */}
      <div className="bg-gradient-to-r from-primary to-primary-dark text-white overflow-hidden relative">
        <div className="flex animate-marquee whitespace-nowrap py-3">
          <div className="flex items-center space-x-12 text-sm font-medium">
            {[...homePage.newsTicker, ...homePage.newsTicker].map((item, index) => {
              const Icon = TICKER_ICONS[index % TICKER_ICONS.length];
              return (
                <div key={index} className="flex items-center space-x-2">
                  <Icon className="w-4 h-4" />
                  <span>{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Hero Carousel Section */}
      <section className="relative py-1 px-4 overflow-hidden bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="relative">
            {/* Carousel Container */}
            <div className="overflow-hidden rounded-2xl relative">
              {/* Navigation Arrows - positioned at description text level across all slides */}
              <button
                onClick={() => setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides)}
                className="absolute left-4 top-40 z-30 bg-white/95 hover:bg-white rounded-full p-3 shadow-xl transition-all duration-300 hover:scale-110 border border-gray-300"
              >
                <svg className="w-6 h-6 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => setCurrentSlide((prev) => (prev + 1) % totalSlides)}
                className="absolute right-4 top-40 z-30 bg-white/95 hover:bg-white rounded-full p-3 shadow-xl transition-all duration-300 hover:scale-110 border border-gray-300"
              >
                <svg className="w-6 h-6 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <HeroCarouselTrack
                  order={heroCarouselOrder}
                  homePage={homePage}
                  carouselHackathons={carouselHackathons}
                  carouselEvents={carouselEvents}
                  mentors={mentors}
                  hackathonFlyerUrls={hackathonFlyerUrls}
                  eventFlyerUrls={eventFlyerUrls}
                  mentorImageUrls={mentorImageUrls}
                  setMentorImageUrls={setMentorImageUrls}
                  isAuthenticated={isAuthenticated}
                  user={user}
                  getDashboardLink={getDashboardLink}
                  getProfileLink={getProfileLink}
                />
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Trust Indicators */}
      <section className="py-8 bg-background">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-500">
            {homePage.trustIndicators.map((item, index) => {
              const TrustIcons = [Users, Calendar, Award];
              const TrustIcon = TrustIcons[index] || Users;
              return (
                <div key={index} className="flex items-center space-x-2">
                  <TrustIcon className="w-5 h-5" />
                  <span className="text-sm font-medium">{item.value} {item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 bg-surface">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-secondary mb-6">
              {homePage.valueProposition.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {homePage.valueProposition.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {homePage.valueProposition.cards.map((card, index) => {
              const cardStyles = [
                { gradient: 'from-primary to-primary-dark', badge: 'bg-success/20', badgeIcon: 'text-success' },
                { gradient: 'from-info to-info/80', badge: 'bg-info/20', badgeIcon: 'text-info' },
                { gradient: 'from-primary-light to-primary', badge: 'bg-primary/20', badgeIcon: 'text-primary' },
              ];
              const CardIcons = [Users, Calendar, TrendingUp];
              const style = cardStyles[index] || cardStyles[0];
              const CardIcon = CardIcons[index] || Users;
              return (
                <div key={index} className="group text-center space-y-6">
                  <div className="relative">
                    <div className={`bg-gradient-to-br ${style.gradient} text-white w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <CardIcon className="w-10 h-10" />
                    </div>
                    <div className={`absolute -top-2 -right-2 w-6 h-6 ${style.badge} rounded-full flex items-center justify-center`}>
                      <CheckCircle className={`w-4 h-4 ${style.badgeIcon}`} />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-2xl font-bold text-secondary">{card.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{card.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-secondary mb-4">
              {homePage.testimonials.title}
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 text-left">
            {homePage.testimonials.items.map((testimonial, index) => (
              <div key={index} className="bg-surface p-8 rounded-2xl shadow-lg border border-border relative">
                <div className="absolute top-0 left-4 -mt-6 text-7xl text-primary opacity-20 font-serif">"</div>
                <p className="text-gray-600 italic leading-relaxed mb-6">
                  "{testimonial.quote}"
                </p>
                <div className="text-right">
                  <p className="font-semibold text-secondary">~ {testimonial.author}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ResumeAnalysisTeaser />

      {/* Sponsors Section */}
      <section className="py-16 bg-surface">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold text-secondary">
                {homePage.sponsorsSection.title}
              </h2>
              <p className="text-xl text-gray-600">
                {homePage.sponsorsSection.subtitle}
              </p>
            </div>

            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              {sponsorsLoading ? (
                <div className="text-gray-500">Loading sponsors...</div>
              ) : sponsors.length > 0 ? (
                sponsors.map((sponsor, index) => (
                  <div key={index} className="group bg-background hover:bg-surface p-8 rounded-2xl transition-all duration-300 border-2 border-border hover:border-warning flex items-center justify-center">
                    {sponsor.logoUrl ? (
                      <img 
                        src={sponsor.logoUrl} 
                        alt={sponsor.name} 
                        className="w-24 h-24 object-contain rounded-lg shadow-lg"
                        onError={(e) => {
                          // Fallback to colored square with initial if image fails
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            const fallback = document.createElement('div');
                            fallback.className = 'w-24 h-24 bg-warning rounded-lg flex items-center justify-center shadow-lg';
                            fallback.innerHTML = `<span class="text-white text-2xl font-bold">${sponsor.name.charAt(0).toUpperCase()}</span>`;
                            parent.appendChild(fallback);
                          }
                        }}
                      />
                    ) : (
                      <div className="w-24 h-24 bg-warning rounded-lg flex items-center justify-center shadow-lg">
                        <span className="text-white text-2xl font-bold">{sponsor.name.charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                // Fallback to default sponsors if none are configured
                <>
                  <div className="group bg-background hover:bg-surface p-8 rounded-2xl transition-all duration-300 border-2 border-border hover:border-warning flex items-center justify-center">
                    <div className="w-24 h-24 bg-warning rounded-lg flex items-center justify-center shadow-lg">
                      <span className="text-white text-2xl font-bold">A</span>
                    </div>
                  </div>

                  <div className="group bg-background hover:bg-surface p-8 rounded-2xl transition-all duration-300 border-2 border-border hover:border-info flex items-center justify-center">
                    <div className="w-24 h-24 bg-info rounded-lg flex items-center justify-center shadow-lg">
                      <span className="text-white text-2xl font-bold">S</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="pt-8">
              <p className="text-gray-500 text-sm">
                {homePage.sponsorsSection.footerNote}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-dark text-white">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <div className="space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold">
              {homePage.finalCta.title}
            </h2>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              {homePage.finalCta.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Link 
                to="/register" 
                className="group bg-surface text-primary px-10 py-4 rounded-xl font-semibold text-lg hover:bg-background hover:text-primary-dark transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center space-x-2"
              >
                <span>{homePage.finalCta.primaryCta}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                to="/events" 
                className="border-2 border-white text-white px-10 py-4 rounded-xl font-semibold text-lg hover:bg-surface hover:text-primary transition-all duration-300 flex items-center space-x-2"
              >
                <span>{homePage.finalCta.secondaryCta}</span>
                <Calendar className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 